import type { FC, ReactNode } from 'react'
import type { MotionValue } from 'motion/react'

import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'
import { ArrowRight, ArrowUpRight } from 'lucide-react'
import { motion, useScroll, useTransform } from 'motion/react'
import { cn } from '@/lib/utils'
import { clamp01 } from '@/lib/format'
import { useIsDesktop, usePrefersReducedMotion } from '@/hooks/use-media-query'
import { RollText } from '@/components/generals/roll-text'
import { gifts, giftsIntro } from '@/constants/content'

type Gift = (typeof gifts)[number]

// Vertical scroll per pixel of sideways travel: below 1 the track moves faster than the page.
const SCROLL_RATIO = 0.72

type TrackMetrics = {
    distance: number
    offsets: Array<number>
    viewport: number
}

interface GiftPanelProps {
    gift: Gift
    index: number
    x: MotionValue<number>
    offset: number
    viewport: number
    horizontal: boolean
}

// One gift. On desktop it reacts to its own position on the horizontal track:
// the photograph opens up and the word widens as the panel slides in.
const GiftPanel: FC<GiftPanelProps> = ({ gift, index, x, offset, viewport, horizontal }): ReactNode => {
    const mirrored = index % 2 === 1
    const local = useTransform(x, (value) => (horizontal ? clamp01(1 - (offset + value) / viewport) : 1))
    const clipPath = useTransform(local, [0, 0.62], ['inset(16% 14% 16% 14%)', 'inset(0% 0% 0% 0%)'])
    const imageScale = useTransform(local, [0, 1], [1.28, 1.04])
    const imageX = useTransform(local, [0, 1], ['7%', '-3%'])
    const variation = useTransform(local, (value) => `'wdth' ${(62 + 38 * clamp01(value / 0.8)).toFixed(1)}, 'wght' 820`)

    return (
        <article
            aria-labelledby={`dhurata-${gift.id}`}
            className={cn(
                'relative shrink-0',
                horizontal
                    ? 'grid h-full w-[68vw] grid-cols-12 grid-rows-[auto_1fr_auto] gap-x-6 px-[4vw] pb-20 pt-28'
                    : 'flex flex-col gap-6 border-t border-tone-line px-5 py-16 md:px-8'
            )}
        >
            <p
                className={cn(
                    'flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.18em] text-tone-muted',
                    horizontal && (mirrored ? 'col-span-6 col-start-7 row-start-1' : 'col-span-6 row-start-1')
                )}
            >
                <span className='font-heading tabular-nums text-tone-accent'>{gift.number}</span>
                <span aria-hidden='true' className='h-px w-8 bg-tone-line' />
                <span>Dhurata {gift.number} nga 04</span>
            </p>

            <motion.div
                style={horizontal ? { clipPath } : undefined}
                className={cn(
                    'relative overflow-hidden bg-jap-purple',
                    horizontal
                        ? (mirrored ? 'col-span-5 col-start-1 row-span-3 row-start-1' : 'col-span-5 col-start-8 row-span-3 row-start-1')
                        : 'aspect-[4/5] w-full sm:aspect-[16/11]'
                )}
            >
                <motion.div style={horizontal ? { scale: imageScale, x: imageX } : undefined} className='absolute inset-0'>
                    <Image
                        src={gift.image}
                        alt={gift.alt}
                        fill
                        sizes='(min-width: 1024px) 34vw, 100vw'
                        className='duotone-lilac object-cover'
                    />
                </motion.div>
            </motion.div>

            <div
                className={cn(
                    'flex flex-col gap-5',
                    horizontal && (mirrored ? 'col-span-6 col-start-7 row-start-3 self-end' : 'col-span-7 row-start-3 self-end')
                )}
            >
                <motion.h3
                    id={`dhurata-${gift.id}`}
                    style={{ fontVariationSettings: horizontal ? variation : "'wdth' 100, 'wght' 820" }}
                    className='font-heading text-[17vw] leading-[0.86] tracking-[-0.05em] text-tone-accent lg:text-[8.6vw]'
                >
                    {gift.word}
                </motion.h3>

                <p className='max-w-md text-lg leading-relaxed text-tone-fg/85 md:text-xl'>
                    {gift.note}
                </p>
            </div>
        </article>
    )
}

interface ProgressProps {
    progress: MotionValue<number>
}

const TrackProgress: FC<ProgressProps> = ({ progress }): ReactNode => {
    const scaleX = useTransform(progress, [0, 1], [0, 1])

    return (
        <div aria-hidden='true' className='absolute inset-x-[4vw] bottom-8 h-px bg-tone-line'>
            <motion.div style={{ scaleX }} className='h-full origin-left bg-tone-accent' />
        </div>
    )
}

// "What we give": four gifts on a pinned horizontal track on desktop, stacked on smaller screens.
export const Gifts: FC = (): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const trackRef = useRef<HTMLDivElement>(null)
    const isDesktop = useIsDesktop()
    const reduced = usePrefersReducedMotion()
    const horizontal = isDesktop && !reduced
    const [metrics, setMetrics] = useState<TrackMetrics>({ distance: 0, offsets: [], viewport: 1 })

    useEffect(() => {
        const track = trackRef.current

        if (!horizontal || !track) return

        const measure = () => {
            setMetrics({
                distance: Math.max(0, track.scrollWidth - window.innerWidth),
                offsets: Array.from(track.children).map((child) => (child as HTMLElement).offsetLeft),
                viewport: window.innerWidth || 1
            })
        }

        measure()

        const observer = new ResizeObserver(measure)

        observer.observe(track)
        window.addEventListener('resize', measure)

        return () => {
            observer.disconnect()
            window.removeEventListener('resize', measure)
        }
    }, [horizontal])

    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ['start start', 'end end']
    })

    const x = useTransform(scrollYProgress, (progress) => (horizontal ? -progress * metrics.distance : 0))

    return (
        <section
            ref={sectionRef}
            id='dhurata'
            data-tone='night'
            aria-labelledby='dhurata-titulli'
            className='relative'
            style={horizontal ? { height: `calc(100svh + ${Math.round(metrics.distance * SCROLL_RATIO)}px)` } : undefined}
        >
            <div className={cn(horizontal ? 'sticky top-0 h-svh overflow-hidden' : 'overflow-hidden')}>
                <motion.div
                    ref={trackRef}
                    style={{ x }}
                    className={cn('relative flex', horizontal ? 'h-full w-max' : 'flex-col')}
                >
                    <div
                        className={cn(
                            'flex shrink-0 flex-col',
                            horizontal ? 'h-full w-[44vw] justify-between px-[4vw] pb-20 pt-28' : 'gap-8 px-5 pb-16 pt-28 md:px-8'
                        )}
                    >
                        <p className='flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.18em] text-tone-muted'>
                            <span className='font-heading tabular-nums text-tone-accent'>03</span>
                            <span aria-hidden='true' className='h-px w-8 bg-tone-line' />
                            {giftsIntro.label}
                        </p>

                        <div className='flex flex-col gap-8'>
                            <h2
                                id='dhurata-titulli'
                                className='max-w-[12ch] font-heading text-5xl font-bold leading-[0.95] tracking-[-0.04em] [font-stretch:105%] sm:text-6xl lg:text-[5.4vw]'
                            >
                                {giftsIntro.title}
                            </h2>

                            <p className='max-w-md text-lg leading-relaxed text-tone-muted md:text-xl'>
                                {giftsIntro.text}
                            </p>
                        </div>

                        {horizontal && (
                            <p className='flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.18em] text-tone-accent'>
                                {giftsIntro.hint}
                                <ArrowRight className='size-4' />
                            </p>
                        )}
                    </div>

                    {gifts.map((gift, index) => (
                        <GiftPanel
                            key={gift.id}
                            gift={gift}
                            index={index}
                            x={x}
                            offset={metrics.offsets[index + 1] ?? 0}
                            viewport={metrics.viewport}
                            horizontal={horizontal}
                        />
                    ))}

                    <div
                        className={cn(
                            'flex shrink-0 flex-col justify-center gap-10',
                            horizontal ? 'h-full w-[46vw] px-[5vw]' : 'border-t border-tone-line px-5 py-24 md:px-8'
                        )}
                    >
                        <p className='font-serif text-5xl italic leading-[1.02] sm:text-6xl lg:text-[5.6vw]'>
                            {giftsIntro.outroTitle}
                        </p>

                        <a
                            href='#bashkohu'
                            className='group inline-flex w-fit items-center gap-3 bg-tone-accent px-6 py-4 text-sm font-semibold text-tone-accent-fg'
                        >
                            <RollText text={giftsIntro.outroCta} />
                            <ArrowUpRight className='size-4 transition-transform duration-500 ease-out-expo group-hover:rotate-45' />
                        </a>
                    </div>
                </motion.div>

                {horizontal && <TrackProgress progress={scrollYProgress} />}
            </div>
        </section>
    )
}
